import { ApiService } from "./apiService";
import { failure } from "./serviceHelpers";
import { IngredientEntity, RecipeServiceResponse } from "../types/recipe";
import { parseMeasurementUnits } from "../utils/measurementUnits";

type RawRow = Record<string, unknown>;

interface IngredientSearchBody {
  data: {
    data?: RawRow[];
    current_page?: number;
    last_page?: number;
    total?: number;
    per_page?: number;
  };
}

export interface IngredientSearchResult {
  items: IngredientEntity[];
  page: number;
  hasMore: boolean;
  total: number;
  lastPage: number;
}

const flattenNutritionGroups = (row: RawRow): RawRow => {
  const { carbs, fat, micronutrients, ...rest } = row;
  return {
    ...rest,
    ...((carbs ?? {}) as RawRow),
    ...((fat ?? {}) as RawRow),
    ...((micronutrients ?? {}) as RawRow),
  };
};

const num = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;
const numOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
const strOrNull = (value: unknown): string | null =>
  typeof value === "string" ? value : null;

const parseIngredientRow = (raw: RawRow): IngredientEntity => {
  const row = flattenNutritionGroups(raw);
  return {
    id: num(row.id),
    name: typeof row.name === "string" ? row.name : "",
    calories_kcal_per_100g: num(row.calories_kcal_per_100g),
    protein_g_per_100g: num(row.protein_g_per_100g),
    carbs_g_per_100g: num(row.carbs_g_per_100g),
    fat_g_per_100g: num(row.fat_g_per_100g),
    fiber_g_per_100g: numOrNull(row.fiber_g_per_100g),
    sodium_mg_per_100g: numOrNull(row.sodium_mg_per_100g),
    default_serving_amount: numOrNull(row.default_serving_amount),
    default_serving_unit: strOrNull(row.default_serving_unit),
    default_serving_grams: numOrNull(row.default_serving_grams),
    default_serving_description: strOrNull(row.default_serving_description),
    measurement_units: parseMeasurementUnits(row.measurement_units),
  };
};

export class IngredientService {
  static async searchIngredients(
    searchTerm: string,
    page: number = 1
  ): Promise<RecipeServiceResponse<IngredientSearchResult>> {
    try {
      const params = new URLSearchParams({ page: page.toString() });
      const term = searchTerm.trim();
      if (term) {
        params.append("searchTerm", term);
      }

      const response = await ApiService.get(
        `/ingredients/search?${params.toString()}`
      );
      const body = await ApiService.handleResponse<IngredientSearchBody>(
        response
      );
      const pag = body.data || {};

      const currentPage = pag.current_page || 1;
      const lastPage = pag.last_page || 1;

      return {
        success: true,
        data: {
          items: (pag.data || []).map(parseIngredientRow),
          page: currentPage,
          hasMore: currentPage < lastPage,
          total: pag.total || 0,
          lastPage,
        },
      };
    } catch (err) {
      return failure(err, "Failed to search ingredients");
    }
  }
}
